/**
 * /erp/orders/{id} — order detail + lifecycle (ERP v1.2).
 *
 * Shows the order header, its lines (with on-hand + due-inventory
 * rollups per product), and the lifecycle action buttons. Submit is
 * open to any user; mark-ready / complete / cancel / delete are
 * manager-only (managers act as production when marking ready). When
 * an order is ready for dispatch, a shortcut links to the new-dispatch
 * form so it can be shipped.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  PackageCheck,
  Send,
  ShoppingCart,
  Trash2,
  Truck,
  XCircle,
} from "lucide-react";

import { ApiError, type ErpOrderResponse, erpApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { useOrder } from "../lib/erp";
import { toast } from "../lib/toast";
import { formatQty } from "../lib/units";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import { ORDER_STATUS_STYLES, orderStatusLabel } from "./_app.erp.orders.index";

export const Route = createFileRoute("/_app/erp/orders/$orderId")({
  component: OrderDetailPage,
});

function OrderDetailPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isManager = user?.role === "admin" || user?.role === "manager";
  const isProduction = user?.role === "production";
  const canMarkReady = isManager || isProduction;

  const orderQ = useOrder(orderId);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["erp", "orders"] });
    queryClient.invalidateQueries({ queryKey: ["erp", "orders", orderId] });
  }

  const action = (fn: () => Promise<ErpOrderResponse | undefined>, ok: string) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        invalidate();
        toast.success(ok);
      },
      onError: (err) => {
        let msg = err instanceof Error ? err.message : "Action failed";
        if (err instanceof ApiError) {
          const detail = err.detail;
          if (typeof detail === "string") msg = detail;
          else if (
            detail &&
            typeof detail === "object" &&
            "message" in detail &&
            typeof (detail as { message: unknown }).message === "string"
          ) {
            msg = (detail as { message: string }).message;
          }
        }
        toast.error(msg);
      },
    });

  const submitMut = action(() => erpApi.orders.submit(orderId), "Order submitted");
  const readyMut = action(() => erpApi.orders.markReady(orderId), "Marked ready for dispatch");
  const completeMut = action(() => erpApi.orders.complete(orderId), "Order completed");
  const cancelMut = action(() => erpApi.orders.cancel(orderId), "Order cancelled");
  const deleteMut = useMutation({
    mutationFn: () => erpApi.orders.remove(orderId),
    onSuccess: () => {
      invalidate();
      toast.success("Order deleted");
      navigate({ to: "/erp/orders" });
    },
    onError: () => toast.error("Could not delete order"),
  });

  if (orderQ.isLoading) {
    return (
      <p className="flex items-center gap-2 p-6 text-sm opacity-60">
        <Loader2 className="size-4 animate-spin" aria-hidden /> Loading order…
      </p>
    );
  }
  if (orderQ.error || !orderQ.data) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Link to="/erp/orders" className="inline-flex items-center gap-1 text-sm opacity-70">
          <ArrowLeft className="size-4" aria-hidden /> Back to orders
        </Link>
        <p className="mt-4 text-sm text-red-700 dark:text-red-300">Order not found.</p>
      </div>
    );
  }

  const o = orderQ.data;
  const commercialOrder = "account_name" in o ? o : null;
  const busy =
    submitMut.isPending ||
    readyMut.isPending ||
    completeMut.isPending ||
    cancelMut.isPending ||
    deleteMut.isPending;

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <div>
        <Link
          to="/erp/orders"
          className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="size-4" aria-hidden /> Back to orders
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ShoppingCart className="size-5 opacity-70" aria-hidden />
            <span className="font-mono">{o.code}</span>
            <span
              className={`rounded-full border px-2 py-0.5 text-xs font-medium ${ORDER_STATUS_STYLES[o.status]}`}
            >
              {orderStatusLabel(o.status)}
            </span>
          </h1>
          <p className="text-sm opacity-60">
            {isProduction
              ? "Production order"
              : commercialOrder?.account_name || commercialOrder?.contact_name || "No CRM link"}
            {o.has_custom && " · contains custom items"}
          </p>
        </div>
      </header>

      {/* Lifecycle actions */}
      <div className="flex flex-wrap gap-2 rounded-xl border border-[var(--border)] p-3">
        {!isProduction && o.status === "draft" && (
          <button
            type="button"
            onClick={() => submitMut.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            <Send className="size-4" aria-hidden /> Submit
          </button>
        )}
        {o.status === "pending_production" && canMarkReady && (
          <button
            type="button"
            onClick={() => readyMut.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            <PackageCheck className="size-4" aria-hidden /> Mark ready for dispatch
          </button>
        )}
        {!isProduction &&
          (o.status === "ready_for_dispatch" || o.status === "partially_fulfilled") && (
          <Link
            to="/erp/dispatches/new"
            search={{ order_id: o.id }}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Truck className="size-4" aria-hidden /> Add to dispatch
          </Link>
        )}
        {!isProduction &&
          (o.status === "ready_for_dispatch" || o.status === "partially_fulfilled") &&
          isManager && (
          <button
            type="button"
            onClick={() => completeMut.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-60"
          >
            <CheckCircle2 className="size-4" aria-hidden /> Mark completed
          </button>
        )}
        {isManager && o.status !== "completed" && o.status !== "cancelled" && (
          <button
            type="button"
            onClick={() => cancelMut.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300 disabled:opacity-60"
          >
            <XCircle className="size-4" aria-hidden /> Cancel
          </button>
        )}
        {isManager && (
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete order ${o.code}? This cannot be undone.`)) deleteMut.mutate();
            }}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300 disabled:opacity-60"
          >
            <Trash2 className="size-4" aria-hidden /> Delete
          </button>
        )}
      </div>

      {commercialOrder?.notes && (
        <p className="rounded-xl border border-[var(--border)] p-3 text-sm">
          {commercialOrder.notes}
        </p>
      )}

      {/* Lines */}
      <div className="overflow-hidden rounded-xl border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wide opacity-60 dark:bg-white/[0.02]">
            <tr>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              <th className="px-3 py-2 text-right font-medium">Shipped</th>
              {!isProduction && (
                <th className="px-3 py-2 text-right font-medium">Unit price</th>
              )}
              <th className="px-3 py-2 text-right font-medium">On hand</th>
              <th className="px-3 py-2 text-right font-medium">Due</th>
              <th className="px-3 py-2 text-center font-medium">Custom</th>
            </tr>
          </thead>
          <tbody>
            {o.lines.map((l) => {
              const shortfall =
                l.on_hand_qty != null && l.due_qty != null
                  ? Number(l.due_qty) > Number(l.on_hand_qty)
                  : false;
              return (
                <tr key={l.id} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-3 py-2">
                    <span className="font-medium">{l.item_name ?? "—"}</span>{" "}
                    <span className="font-mono text-xs opacity-60">{l.item_code}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatQty(l.qty, l.item_uom)}
                    {l.item_uom ? <span className="opacity-50"> {l.item_uom}</span> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {Number(l.qty_fulfilled) > 0 ? (
                      <span>
                        {formatQty(l.qty_fulfilled, l.item_uom)}
                        {l.qty_remaining && Number(l.qty_remaining) > 0 && (
                          <span className="text-indigo-600 dark:text-indigo-400">
                            {" "}
                            · {formatQty(l.qty_remaining, l.item_uom)} left
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="opacity-40">—</span>
                    )}
                  </td>
                  {!isProduction && (
                    <td className="px-3 py-2 text-right tabular-nums">
                      {"unit_price" in l && l.unit_price ? (
                        `₹${l.unit_price}`
                      ) : (
                        <span className="opacity-40">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right tabular-nums">
                    {l.on_hand_qty != null ? (
                      formatQty(l.on_hand_qty, l.item_uom)
                    ) : (
                      <span className="opacity-40">—</span>
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${shortfall ? "text-amber-700 dark:text-amber-300" : ""}`}
                  >
                    {l.due_qty != null ? (
                      formatQty(l.due_qty, l.item_uom)
                    ) : (
                      <span className="opacity-40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {l.is_custom ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                        custom
                      </span>
                    ) : (
                      <span className="opacity-30">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {o.lines.length === 0 && (
              <tr>
                <td
                  colSpan={isProduction ? 6 : 7}
                  className="px-3 py-6 text-center text-sm opacity-50"
                >
                  No lines on this order.
                </td>
              </tr>
            )}
          </tbody>
          {commercialOrder?.order_total && (
            <tfoot className="border-t border-[var(--border)]">
              <tr>
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                  Total: ₹{commercialOrder.order_total}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-[11px] opacity-60">
        <strong>On hand</strong> is total stock across all warehouses. <strong>Due</strong> is the
        quantity promised across all open orders for that item (amber when it exceeds stock).
      </p>
      <CustomFieldsPanel entity="orders" recordKey={o.id} />
    </div>
  );
}
